/**
 * ProShip Direct v2 — shared shell for the five courier-setup pages.
 *
 * Serviceability, zones, TAT and the rate card all need the same four things: the
 * plan to be active, an own account chosen, a statement of what happens when the merchant
 * supplies nothing, and a way to hand control back to the ProShip default. Building that
 * four times would be the copy-paste this repo's standards call debt (§4.2), so it lives here and
 * each page renders its body through the render prop.
 *
 * EMBEDDED is how Courier Setup uses it. They are tabs of one page now, so the tabbed page
 * owns the container and the title and the shell renders only from the account picker down —
 * otherwise every tab switch would redraw a page heading identical to the one above it.
 *
 * THE ACCOUNT LIVES IN THE URL, not just in state. Tabs are separate renders of this shell, so a
 * selection held only here would reset to the first account the moment the merchant moved from
 * Coverage to Zones — which is precisely when they least expect it, mid-way through setting up
 * one account. Writing it back to ?account= makes the choice survive the tab, the reload and the
 * copied link.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, MenuItem, Select, Button, Alert } from '@mui/material';
import PageTitle from '../../../common/PageTitle';
import { pageContainerSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import { NAVY, SUBTLE, BORDER, WHITE, LIGHT_BG } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { usePlan, useOwnCouriers, useDirectAccounts } from '../data/hooks';
import { ProvenanceChip, OwnerChip, CredentialChip } from './chips';
import { PlanInactive, NoAccounts } from './states';

const pickerRowSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: GAP.md,
  bgcolor: WHITE,
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS.md,
  p: 1.5,
};

const labelSx = {
  fontFamily: FONT,
  fontSize: SIZE['4xs'],
  fontWeight: WEIGHT.semibold,
  color: SUBTLE,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

/**
 * @param serverManaged  true when the child owns its own state from the server, as Serviceability
 *   now does. The shell still lists accounts and lets the merchant pick one, but does not render
 *   the fixture-backed "Provide my own / Revert to ProShip default" banner — that control writes
 *   to browser storage, and on a dimension with a real backend it would say one thing while the
 *   server held another.
 */
export default function SetupPageShell({ dimension, children, embedded = false, serverManaged = false }) {
  const { plan } = usePlan();
  /*
    The account LIST is real — the merchant's registered Direct couriers. `setConfig` is still the
    fixture store, and still correct for the three dimensions whose uploads are unbuilt; the one
    that has a backend passes serverManaged and ignores it.
  */
  const { couriers } = useDirectAccounts();
  const { setConfig } = useOwnCouriers();
  const claimedByUpload = Boolean(dimension.claimedByUpload);
  // ?account=own-2 — set by the edit affordance in My Couriers, so arriving here from a specific
  // row lands on that row's account instead of the first one in the list.
  const [params, setParams] = useSearchParams();
  const requested = params.get('account');
  const [accountId, setAccountId] = useState(requested || '');

  /** Chosen here, remembered in the URL, so the next tab opens on the same account. */
  const chooseAccount = (id) => {
    setAccountId(id);
    const next = new URLSearchParams(params);
    next.set('account', id);
    setParams(next, { replace: true });
  };

  // Keep the selection valid when accounts are added or removed elsewhere.
  useEffect(() => {
    if (!couriers.length) {
      setAccountId('');
      return;
    }
    if (!couriers.some((c) => c.id === accountId)) {
      const asked = couriers.find((c) => c.id === requested);
      setAccountId(asked ? asked.id : couriers[0].id);
    }
  }, [couriers, accountId, requested]);

  const account = couriers.find((c) => c.id === accountId);
  const provided = account?.config?.[dimension.key] === 'OWN';

  const body = () => {
    if (!plan.active) return <PlanInactive what='Courier setup' />;
    if (!couriers.length) return <NoAccounts />;
    if (!account) return null;

    return (
      <>
        <Box sx={pickerRowSx}>
          <Typography sx={labelSx}>Courier account</Typography>
          <Select
            size='small'
            value={accountId}
            onChange={(e) => chooseAccount(e.target.value)}
            sx={{ minWidth: 280, fontFamily: FONT, fontSize: SIZE['2xs'] }}
          >
            {couriers.map((c) => (
              <MenuItem key={c.id} value={c.id} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                {c.carrierName} · {c.accountCode}
              </MenuItem>
            ))}
          </Select>
          <OwnerChip owner='OWN' />
          <CredentialChip state={account.credentials} />
          <Box sx={{ flex: 1 }} />
          <ProvenanceChip provided={provided} label={dimension.label} />
        </Box>

        {/*
          WHO OWNS THIS SETTING IS DECIDED BY THE DATA, NOT BY A BUTTON — wherever there is data to
          own. On the upload dimensions (coverage, zones, rate card) the file is the claim: saving
          one sets OWN, and a "Provide my own" button beside a drop zone that works regardless only
          offered a way to claim ownership of nothing, leaving the page insisting the merchant's
          own coverage was in force with no file behind it. Those pages pass claimedByUpload and
          get no button until there is something to revert.

          Tat and Status mapping keep it: there is no file there, so taking over ProShip's values
          to edit them is a real action that nothing else expresses.

          A required dimension has no ProShip default to fall back to or revert to, so it warns
          rather than informs and offers no way back.
        */}
        {!serverManaged && (
        <Alert
          severity={provided ? 'success' : dimension.required ? 'warning' : 'info'}
          sx={{
            fontFamily: FONT,
            fontSize: SIZE['2xs'],
            alignItems: 'center',
            bgcolor: provided || dimension.required ? undefined : LIGHT_BG,
          }}
          action={
            (provided && dimension.required) || (!provided && claimedByUpload) ? null : (
              <Button
                size='small'
                onClick={() => setConfig(account.id, dimension.key, !provided)}
                sx={provided ? secondaryBtnSx : primaryBtnSx}
              >
                {provided ? 'Revert to ProShip default' : 'Provide my own'}
              </Button>
            )
          }
        >
          {provided ? `Using your own ${dimension.label.toLowerCase()}.` : dimension.fallback}
        </Alert>
        )}

        {children({ account, provided, setProvided: (v) => setConfig(account.id, dimension.key, v) })}
      </>
    );
  };

  const stack = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg, color: NAVY }}>{body()}</Box>
  );

  if (embedded) return stack;

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle={dimension.blurb}>{dimension.label}</PageTitle>
      {stack}
    </Box>
  );
}
